import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Megaphone, Plus, Trash2, ToggleLeft, ToggleRight, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

export default function AdminAnnouncements() {
  const { token, API } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [type, setType] = useState('info');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/announcements`, { headers: { Authorization: `Bearer ${token}` } });
      setAnnouncements(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchAnnouncements(); }, [fetchAnnouncements]);

  const createAnnouncement = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/announcements`, { title, message, type }, { headers: { Authorization: `Bearer ${token}` } });
      setTitle('');
      setMessage('');
      setType('info');
      setShowForm(false);
      fetchAnnouncements();
    } catch (e) { alert('Failed to create'); }
    setCreating(false);
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchAnnouncements();
    } catch (e) { alert('Failed'); }
  };

  const toggleAnnouncement = async (id) => {
    try {
      await axios.put(`${API}/admin/announcements/${id}/toggle`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchAnnouncements();
    } catch (e) { alert('Failed'); }
  };

  const typeConfig = {
    info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', badge: 'bg-blue-500/10 text-blue-400' },
    warning: { icon: AlertCircle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', badge: 'bg-amber-500/10 text-amber-400' },
    success: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', badge: 'bg-emerald-500/10 text-emerald-400' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">{announcements.length} announcements</p>
        <button
          data-testid="admin-new-announcement-btn"
          onClick={() => setShowForm(!showForm)}
          className="honey-btn px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2"
        >
          <Plus className="w-3.5 h-3.5" />
          New Announcement
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <motion.form
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={createAnnouncement}
          className="glass-card rounded-xl p-6 space-y-4 border border-amber-500/20"
        >
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Title</label>
            <Input
              data-testid="admin-announcement-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Announcement title"
              className="bg-[#09090B] border-white/10 text-white"
              required
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Message</label>
            <Textarea
              data-testid="admin-announcement-message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Write your announcement..."
              className="bg-[#09090B] border-white/10 text-white min-h-[80px] resize-none"
              required
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">Type</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="admin-announcement-type" className="bg-[#09090B] border-white/10 text-white w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121214] border-white/10">
                <SelectItem value="info" className="text-white">Info</SelectItem>
                <SelectItem value="warning" className="text-white">Warning</SelectItem>
                <SelectItem value="success" className="text-white">Success</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-lg text-xs bg-white/5 text-zinc-400 hover:bg-white/10"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="admin-announcement-submit"
              disabled={creating}
              className="honey-btn px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-50"
            >
              {creating ? 'Publishing...' : 'Publish'}
            </button>
          </div>
        </motion.form>
      )}

      {/* Announcements List */}
      <div className="space-y-3">
        {announcements.map((a, i) => {
          const tc = typeConfig[a.type] || typeConfig.info;
          const TypeIcon = tc.icon;

          return (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className={`glass-card rounded-xl p-5 border ${a.active ? tc.border : 'border-white/5 opacity-50'}`}
              data-testid={`admin-announcement-${i}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-9 h-9 rounded-xl ${tc.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <TypeIcon className={`w-4 h-4 ${tc.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-bold text-white">{a.title}</h4>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${tc.badge}`}>
                      {a.type}
                    </span>
                    {!a.active && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-500">
                        inactive
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-zinc-400 leading-relaxed">{a.message}</p>
                  <p className="text-[10px] text-zinc-600 mt-2">
                    By {a.created_by} &middot; {new Date(a.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    data-testid={`admin-toggle-announcement-${i}`}
                    onClick={() => toggleAnnouncement(a.id)}
                    className={`p-1.5 rounded-md transition-colors ${
                      a.active ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-zinc-600 hover:bg-white/5'
                    }`}
                    title={a.active ? 'Deactivate' : 'Activate'}
                  >
                    {a.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                  </button>
                  <button
                    data-testid={`admin-delete-announcement-${i}`}
                    onClick={() => deleteAnnouncement(a.id)}
                    className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}

        {announcements.length === 0 && (
          <div className="text-center py-16">
            <Megaphone className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No announcements yet</p>
            <p className="text-xs text-zinc-600">Create one to notify all users</p>
          </div>
        )}
      </div>
    </div>
  );
}
